<template>
  <div>
    <div
      v-if="!isReadOnly"
      class="mb-3 d-flex flex-wrap align-items-center"
      style="gap: 8px"
    >
      <a-select
        v-model="bulkChoice"
        style="width: min(320px, 100%)"
        :placeholder="$t('decisionConseil.selectDecision')"
      >
        <a-select-option value="auto">{{
          $t("decisionConseil.auto")
        }}</a-select-option>
        <a-select-option
          v-for="p in boardPresets"
          :key="p.id"
          :value="'p-' + p.id"
        >
          {{ presetLabel(p) }}
        </a-select-option>
        <a-select-option value="other">{{
          $t("decisionConseil.other")
        }}</a-select-option>
      </a-select>
      <a-input
        v-if="bulkChoice === 'other'"
        v-model="bulkManualText"
        style="width: 200px"
        :placeholder="$t('decisionConseil.manualPlaceholder')"
      />
      <PvDecisionKeySelect
        v-if="bulkChoice === 'other'"
        v-model="bulkManualDecisonOn"
        :select-style="{ width: '200px' }"
      />
      <a-button @click="stageBulk">
        {{ $t("decisionConseil.applyToSelected") }}
      </a-button>
      <a-button
        type="primary"
        :disabled="!hasUnsavedChanges"
        :loading="saving"
        @click="saveAll"
      >
        {{ $t("action.enregistrer") }}
      </a-button>
      <a-button
        :disabled="!hasUnsavedChanges || saving"
        @click="discardChanges"
      >
        {{ $t("action.annuler") }}
      </a-button>
    </div>

    <a-alert
      v-if="hasUnsavedChanges && !isReadOnly"
      class="mb-3"
      type="info"
      show-icon
      :message="$t('decisionConseil.unsavedHint')"
    />

    <a-alert
      v-if="studentsMissingPvKey.length"
      class="mb-3"
      type="warning"
      show-icon
    >
      <template slot="message">
        {{
          $t("decisionConseil.pvMissingKeyBanner", {
            count: studentsMissingPvKey.length,
          })
        }}
      </template>
      <template slot="description">
        <div>{{ $t("decisionConseil.pvMissingKeyHint") }}</div>
        <ul class="mb-0 mt-2 pl-3">
          <li v-for="s in studentsMissingPvKey.slice(0, 8)" :key="s.id">
            {{ s.fullName }}
          </li>
          <li v-if="studentsMissingPvKey.length > 8">
            {{
              $t("decisionConseil.pvMissingKeyMore", {
                count: studentsMissingPvKey.length - 8,
              })
            }}
          </li>
        </ul>
      </template>
    </a-alert>

    <div class="mb-3">
      <a-input-search
        v-model="studentNameSearch"
        allow-clear
        style="max-width: 320px"
        :placeholder="$t('decisionConseil.filterByStudentName')"
      />
    </div>

    <a-table
      :rowKey="(r) => r.id"
      :columns="columns"
      :data-source="filteredStudents"
      :loading="loading"
      :row-selection="isReadOnly ? null : rowSelection"
      :row-class-name="rowClassName"
      :pagination="{ pageSize: 15 }"
      size="middle"
    >
      <template slot="years_1" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[0]"
          :disabled="level < 1"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="years_2" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[1]"
          :disabled="level < 2"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="years_3" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[2]"
          :disabled="level < 3"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="years_4" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[3]"
          :disabled="level < 4"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="years_5" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[4]"
          :disabled="level < 5"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="years_6" slot-scope="text, record">
        <a-input
          class="components-input-size"
          size="small"
          type="number"
          :min="1"
          v-model="record.years[5]"
          :disabled="level < 6"
          @change="(_) => levelYearChanged(record)"
        />
      </template>
      <template slot="decisionConseil" slot-scope="text, record">
        <a-tag :color="isRowDirty(record.id) ? 'orange' : undefined">
          {{ getDisplayDecision(record) }}
        </a-tag>
      </template>
      <template slot="pvKey" slot-scope="text, record">
        <a-tag
          v-if="isValidPvDecisionKey(resolveRowDecisionKey(record))"
          color="blue"
          dir="rtl"
        >
          {{ decisonOnLabel(resolveRowDecisionKey(record)) }}
        </a-tag>
        <a-tooltip v-else :title="$t('decisionConseil.pvMissingKeyRow')">
          <a-tag color="red">{{ $t("decisionConseil.pvNotDisplayed") }}</a-tag>
        </a-tooltip>
      </template>
      <template slot="decisionSelect" slot-scope="text, record">
        <div class="d-flex flex-column" style="gap: 6px">
          <a-select
            :value="getRowChoice(record)"
            :disabled="isReadOnly"
            style="width: 100%"
            @change="(val) => onRowChoiceChange(record, val)"
          >
            <a-select-option value="auto">{{
              $t("decisionConseil.auto")
            }}</a-select-option>
            <a-select-option
              v-for="p in boardPresets"
              :key="p.id"
              :value="'p-' + p.id"
            >
              {{ presetLabel(p) }}
            </a-select-option>
            <a-select-option value="other">{{
              $t("decisionConseil.other")
            }}</a-select-option>
          </a-select>
          <a-input
            v-if="showManualInput(record)"
            :value="getManualDraft(record)"
            :disabled="isReadOnly"
            :placeholder="$t('decisionConseil.manualPlaceholder')"
            @change="(e) => setManualDraft(record, e.target.value)"
          />
          <PvDecisionKeySelect
            v-if="showManualInput(record)"
            :value="getManualDecisonOnDraft(record)"
            :disabled="isReadOnly"
            @change="(val) => setManualDecisonOn(record, val)"
          />
        </div>
      </template>
    </a-table>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapGetters, mapState } from "vuex";
import { showApiError } from "@/utilities/showApiError";
import PvDecisionKeySelect from "@/components/PvDecisionKeySelect.vue";
import {
  BOARD_DECISION_PRESETS,
  PRESET_TO_KEY,
  getKeyFromDecisionParams,
  isValidPvDecisionKey,
  normalizeDecisionParams,
  pickPresetForGender,
  presetOptionLabel,
  pvDecisionKeyLabel,
} from "@/constants/boardDecisionPresets";

export default {
  name: "DecisionConseilEditor",
  components: { PvDecisionKeySelect },
  props: {
    classroomID: { type: String, default: null },
    type: { type: String, required: true },
    level: { type: Number, required: true },
  },
  data() {
    return {
      loading: false,
      saving: false,
      students: [],
      studentNameSearch: "",
      selectedRowKeys: [],
      bulkChoice: "auto",
      bulkManualText: "",
      bulkManualDecisonOn: undefined,
      pendingChanges: {},
      pendingLevelChanges: {},
      autoDecisionParams: [],
      studentLevelYears: [],
    };
  },
  computed: {
    ...mapGetters(["packDetailsGetter"]),
    ...mapState(["settings"]),
    isReadOnly() {
      return this.packDetailsGetter?.access?.edManager?.boardDecision === "ro";
    },
    boardPresets() {
      return BOARD_DECISION_PRESETS;
    },
    filteredStudents() {
      const q = (this.studentNameSearch || "").trim().toLowerCase();
      if (!q) return this.students;
      return this.students.filter((s) =>
        (s.fullName || "").toLowerCase().includes(q)
      );
    },
    hasUnsavedChanges() {
      return (
        Object.keys(this.pendingChanges).some((id) => this.isRowDirty(id)) ||
        Object.keys(this.pendingLevelChanges).length > 0
      );
    },
    studentsMissingPvKey() {
      return this.students.filter(
        (s) => !isValidPvDecisionKey(this.resolveRowDecisionKey(s))
      );
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys) => {
          this.selectedRowKeys = keys;
        },
      };
    },
    columns() {
      return [
        {
          title: this.$t("menu.eleves"),
          dataIndex: "fullName",
          key: "fullName",
        },
        {
          title: this.$t("bulletin.moyenneGeneral"),
          dataIndex: "finalMoyenne",
          key: "finalMoyenne",
          width: 100,
        },
        {
          title: this.$t("bulletin.decisionConseil"),
          dataIndex: "decisionConseil",
          key: "decisionConseil",
          scopedSlots: { customRender: "decisionConseil" },
        },
        {
          title: this.$t("decisionConseil.years"),
          key: "years",
          children: [
            {
              title: 1,
              dataIndex: `years_1`,
              key: `years_1`,
              scopedSlots: { customRender: "years_1" },
            },
            {
              title: 2,
              dataIndex: `years_2`,
              key: `years_2`,
              scopedSlots: { customRender: "years_2" },
            },
            {
              title: 3,
              dataIndex: `years_3`,
              key: `years_3`,
              scopedSlots: { customRender: "years_3" },
            },
            {
              title: 4,
              dataIndex: `years_4`,
              key: `years_4`,
              scopedSlots: { customRender: "years_4" },
            },
            {
              title: 5,
              dataIndex: `years_5`,
              key: `years_5`,
              scopedSlots: { customRender: "years_5" },
            },
            {
              title: 6,
              dataIndex: `years_6`,
              key: `years_6`,
              scopedSlots: { customRender: "years_6" },
            },
          ],
        },
        {
          title: this.$t("certification.Corresponding"),
          key: "pvKey",
          scopedSlots: { customRender: "pvKey" },
          width: 180,
        },
        {
          title: this.$t("decisionConseil.decision"),
          key: "decisionSelect",
          scopedSlots: { customRender: "decisionSelect" },
          width: 320,
        },
      ];
    },
  },
  watch: {
    classroomID: {
      handler(val) {
        this.resetBulkState();
        if (val) this.loadData();
        else this.students = [];
      },
      immediate: true,
    },
  },
  methods: {
    isValidPvDecisionKey,
    rowClassName(record) {
      if (this.isRowDirty(record.id)) return "decision-conseil-row-pending";
      if (!isValidPvDecisionKey(this.resolveRowDecisionKey(record))) {
        return "decision-conseil-row-no-pv-key";
      }
      return "";
    },
    decisonOnLabel(key) {
      return pvDecisionKeyLabel(key);
    },
    hasValidMoyenne(student) {
      const m = student.finalMoyenne;
      if (m == null || m === "" || m === "--") return false;
      return !Number.isNaN(Number(m));
    },
    canAssignDecision(record, choice) {
      if (choice === "auto") return true;
      return this.hasValidMoyenne(record);
    },
    warnNoMoyenne() {
      this.$message.warning(this.$t("decisionConseil.noMoyenneForStudent"));
    },
    presetLabel(p) {
      return presetOptionLabel(p);
    },
    resetBulkState() {
      this.selectedRowKeys = [];
      this.bulkChoice = "auto";
      this.bulkManualText = "";
      this.bulkManualDecisonOn = undefined;
      this.pendingChanges = {};
      this.pendingLevelChanges = {};
      this.studentNameSearch = "";
    },
    discardChanges() {
      this.pendingChanges = {};
      this.pendingLevelChanges = {};
      this.resetLevelStudents();
    },
    resetLevelStudents() {
      this.students = this.students.map((s) => ({
        ...s,
        years: this.getYearlyrecord(s.id),
      }));
    },
    savedChoice(record) {
      const o = record.decisionConseilOverride;
      if (!o) return "auto";
      if (o.kind === "manual") return "other";
      if (o.kind === "preset") return "p-" + o.decisionConseilId;
      return "auto";
    },
    savedManualText(record) {
      const o = record.decisionConseilOverride;
      if (o && o.kind === "manual") return o.manualText || "";
      return "";
    },
    savedManualDecisonOn(record) {
      const o = record.decisionConseilOverride;
      if (o && o.kind === "manual") return o.decisonOn;
      return undefined;
    },
    isRowDirty(studentId) {
      const pending = this.pendingChanges[studentId];
      const levelPending = this.pendingLevelChanges[studentId];
      if (levelPending) return true;
      if (!pending) return false;
      const record = this.students.find((s) => s.id === studentId);
      if (!record) return false;
      if (pending.choice !== this.savedChoice(record)) return true;
      if (pending.choice === "other") {
        if (
          (pending.manualText || "").trim() !==
          this.savedManualText(record).trim()
        ) {
          return true;
        }
        if (
          (pending.decisonOn || "") !==
          (this.savedManualDecisonOn(record) || "")
        ) {
          return true;
        }
      }
      return false;
    },
    getRowChoice(record) {
      const pending = this.pendingChanges[record.id];
      if (pending) return pending.choice;
      return this.savedChoice(record);
    },
    showManualInput(record) {
      return this.getRowChoice(record) === "other";
    },
    getManualDraft(record) {
      const pending = this.pendingChanges[record.id];
      if (pending && pending.choice === "other") {
        return pending.manualText != null
          ? pending.manualText
          : this.savedManualText(record);
      }
      return this.savedManualText(record);
    },
    getManualDecisonOnDraft(record) {
      const pending = this.pendingChanges[record.id];
      if (pending && pending.choice === "other") {
        return pending.decisonOn != null
          ? pending.decisonOn
          : this.savedManualDecisonOn(record);
      }
      return this.savedManualDecisonOn(record);
    },
    setManualDraft(record, value) {
      if (!this.canAssignDecision(record, "other")) {
        this.warnNoMoyenne();
        return;
      }
      const existing = this.pendingChanges[record.id] || {};
      this.$set(this.pendingChanges, record.id, {
        choice: "other",
        manualText: value,
        decisonOn:
          existing.decisonOn != null
            ? existing.decisonOn
            : this.savedManualDecisonOn(record),
      });
    },
    setManualDecisonOn(record, value) {
      if (!this.canAssignDecision(record, "other")) {
        this.warnNoMoyenne();
        return;
      }
      const existing = this.pendingChanges[record.id] || {};
      this.$set(this.pendingChanges, record.id, {
        choice: "other",
        manualText:
          existing.manualText != null
            ? existing.manualText
            : this.getManualDraft(record),
        decisonOn: value,
      });
    },
    levelYearChanged(record) {
      this.$set(this.pendingLevelChanges, record.id, record.years);
    },
    onRowChoiceChange(record, val) {
      if (this.isReadOnly) return;
      if (!this.canAssignDecision(record, val)) {
        this.warnNoMoyenne();
        return;
      }
      if (val === "other") {
        this.$set(this.pendingChanges, record.id, {
          choice: "other",
          manualText:
            this.getManualDraft(record) || record.decisionConseil || "",
          decisonOn: this.savedManualDecisonOn(record),
        });
        return;
      }
      this.$set(this.pendingChanges, record.id, { choice: val });
    },
    resolveAutoFromSettings(record) {
      if (!this.hasValidMoyenne(record)) return "--";
      const moyenne = Number(record.finalMoyenne);
      for (const d of this.autoDecisionParams) {
        if (moyenne >= Number(d.min) && moyenne <= Number(d.max)) {
          return pickPresetForGender(d, record.gender);
        }
      }
      return "--";
    },
    resolveRowDecisionKey(record) {
      const pending = this.pendingChanges[record.id];
      const choice = pending ? pending.choice : this.savedChoice(record);

      if (choice === "auto") {
        const fromParams = getKeyFromDecisionParams(
          this.autoDecisionParams,
          record.finalMoyenne
        );
        if (isValidPvDecisionKey(fromParams)) return fromParams;
        if (isValidPvDecisionKey(record.decisionConseilKey)) {
          return record.decisionConseilKey;
        }
        return fromParams;
      }

      if (String(choice).startsWith("p-")) {
        const paramId = Number(String(choice).slice(2));
        return PRESET_TO_KEY[paramId] || "--";
      }

      if (choice === "other") {
        const decisonOn = pending
          ? pending.decisonOn
          : this.savedManualDecisonOn(record);
        return decisonOn || "--";
      }

      return "--";
    },
    previewDecision(record, choice, manualText) {
      if (choice === "auto") {
        return this.resolveAutoFromSettings(record);
      }
      if (choice === "other") {
        return (manualText || "").trim() || "—";
      }
      if (String(choice).startsWith("p-")) {
        const paramId = Number(String(choice).slice(2));
        const p = BOARD_DECISION_PRESETS.find((x) => Number(x.id) === paramId);
        if (!p) return "—";
        return pickPresetForGender(p, record.gender);
      }
      return record.decisionConseil || "--";
    },
    getDisplayDecision(record) {
      const pending = this.pendingChanges[record.id];
      if (pending && this.isRowDirty(record.id)) {
        return this.previewDecision(record, pending.choice, pending.manualText);
      }
      if (this.savedChoice(record) === "auto") {
        return this.resolveAutoFromSettings(record);
      }
      return record.decisionConseil || "--";
    },
    buildBody(choice, manualText, decisonOn) {
      if (choice === "auto") return { clear: true };
      if (choice === "other") {
        return {
          manualText: (manualText || "").trim(),
          decisonOn,
        };
      }
      if (String(choice).startsWith("p-")) {
        return { decisionConseilId: Number(String(choice).slice(2)) };
      }
      return { clear: true };
    },
    applyResultToStudent(studentId, data) {
      const idx = this.students.findIndex((s) => s.id === studentId);
      if (idx === -1) return;
      const row = { ...this.students[idx] };
      if (data) {
        row.decisionConseilOverride = data.override;
        if (data.decisionConseil != null) {
          row.decisionConseil = data.decisionConseil;
        }
        if (data.decisionConseilKey != null) {
          row.decisionConseilKey = data.decisionConseilKey;
        }
      }
      this.$set(this.students, idx, row);
    },
    async patchOneLevel(studentId, levels) {
      const payload = { levels };
      const res = await apiClient.patch(
        `/bulletin/v2/decision-conseil/levels/${studentId}`,
        payload
      );
      const idx = this.studentLevelYears.findIndex((s) => s.id === studentId);
      if (idx === -1) return;
      this.studentLevelYears[idx] = levels;
      return true;
    },
    async patchOne(studentId, body, record) {
      const payload = { ...body };
      if (body.clear && record) {
        payload.finalMoyenne = record.finalMoyenne;
      }
      const res = await apiClient.patch(
        `/bulletin/v2/decision-conseil/${studentId}`,
        payload
      );
      this.applyResultToStudent(studentId, res.data.data);
      return res;
    },
    stageBulk() {
      if (this.isReadOnly) return;
      if (!this.selectedRowKeys.length) {
        this.$message.warning(this.$t("decisionConseil.selectStudents"));
        return;
      }
      if (this.bulkChoice === "other") {
        if (!this.bulkManualText.trim()) {
          this.$message.warning(this.$t("decisionConseil.manualPlaceholder"));
          return;
        }
        if (!this.bulkManualDecisonOn) {
          this.$message.warning(this.$t("decisionConseil.decisonOnRequired"));
          return;
        }
      }

      const eligible = this.selectedRowKeys.filter((id) => {
        const student = this.students.find((s) => s.id === id);
        return student && this.hasValidMoyenne(student);
      });

      if (!eligible.length) {
        this.$message.warning(this.$t("decisionConseil.noEligibleStudents"));
        return;
      }

      const selected = this.selectedRowKeys.length;
      const eligibleCount = eligible.length;

      this.$confirm({
        title: this.$t("decisionConseil.bulkConfirmTitle"),
        content: (h) =>
          h("div", [
            h("p", this.$t("decisionConseil.bulkConfirmContent")),
            h(
              "p",
              { style: { marginTop: "8px", fontWeight: "bold" } },
              this.$t("decisionConseil.bulkConfirmCounts", {
                eligible: eligibleCount,
                selected,
              })
            ),
          ]),
        onOk: () => {
          for (const id of eligible) {
            if (this.bulkChoice === "other") {
              this.$set(this.pendingChanges, id, {
                choice: "other",
                manualText: this.bulkManualText.trim(),
                decisonOn: this.bulkManualDecisonOn,
              });
            } else {
              this.$set(this.pendingChanges, id, { choice: this.bulkChoice });
            }
          }
        },
      });
    },
    async saveAll() {
      if (this.isReadOnly || !this.hasUnsavedChanges) return;

      const dirtyIds = Object.keys(this.pendingChanges).filter((id) =>
        this.isRowDirty(id)
      );

      for (const id of dirtyIds) {
        const pending = this.pendingChanges[id];
        const record = this.students.find((s) => s.id === id);
        if (record && !this.canAssignDecision(record, pending.choice)) {
          this.warnNoMoyenne();
          return;
        }
        if (pending.choice === "other") {
          if (!(pending.manualText || "").trim()) {
            this.$message.warning(this.$t("decisionConseil.manualPlaceholder"));
            return;
          }
          if (!pending.decisonOn) {
            this.$message.warning(this.$t("decisionConseil.decisonOnRequired"));
            return;
          }
        }
      }

      this.saving = true;
      let hasClear = false;
      try {
        const clearIds = dirtyIds.filter((id) => {
          const p = this.pendingChanges[id];
          return p && this.buildBody(p.choice).clear;
        });
        const patchIds = dirtyIds.filter((id) => !clearIds.includes(id));

        if (clearIds.length) {
          hasClear = true;
          const res = await apiClient.patch(
            "/bulletin/v2/decision-conseil/bulk",
            {
              studentIds: clearIds,
              clear: true,
              finalMoyennes: clearIds.reduce((acc, id) => {
                const row = this.students.find((s) => s.id === id);
                if (row) acc[id] = row.finalMoyenne;
                return acc;
              }, {}),
            }
          );
          for (const item of res.data.data || []) {
            this.applyResultToStudent(item.studentId, item);
          }
        }

        await Promise.all(
          (Object.entries(this.pendingLevelChanges) || []).map(
            async ([studentID, levels]) =>
              await this.patchOneLevel(studentID, levels)
          ),
          ...patchIds.map(async (id) => {
            const pending = this.pendingChanges[id];
            const record = this.students.find((s) => s.id === id);
            const body =
              pending.choice === "other"
                ? this.buildBody("other", pending.manualText, pending.decisonOn)
                : this.buildBody(pending.choice);
            await this.patchOne(id, body, record);
          })
        );
        this.pendingChanges = {};
        this.pendingLevelChanges = {};
        if (hasClear) await this.loadData();
        this.$message.success(this.$t("success.decisionConseilApplied"));
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.saving = false;
      }
    },
    getYearlyrecord(id) {
      return (
        this.studentLevelYears.find((s) => s.id == id)?.years || [1, 1, 1, 1, 1, 1]
      );
    },
    async loadData() {
      if (!this.classroomID) return;
      this.loading = true;
      try {

        const [yearlyRes, settingsRes, studentYearly] = await Promise.all([
          apiClient.get(
            `/bulletin/v2/bt/yearly/classroom/3/${this.type}/${this.classroomID}`
          ),
          apiClient.get("/decisionConseil"),
          apiClient.get(`/bulletin/v2/decision-conseil/classroom/${this.classroomID}/${this.settings.activeSchoolarYear}`),
        ]);
        this.studentLevelYears = studentYearly.data || [];
        this.students = (yearlyRes.data || []).map((s) => ({
          ...s,
          id: String(s.id),
          years: this.getYearlyrecord(String(s.id)),
        }));
        const raw = settingsRes.data;
        const list = Array.isArray(raw)
          ? raw
          : Object.values(raw || {}).filter(
              (x) => x && (x.id != null || x.key != null)
            );
        this.autoDecisionParams = normalizeDecisionParams(list);
        this.pendingChanges = {};
        this.pendingLevelChanges = {};
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .decision-conseil-row-pending td {
  background-color: #fffbe6;
}
::v-deep .decision-conseil-row-no-pv-key td {
  background-color: #fff1f0;
}
.components-input-size {
  width: 60px;
}
</style>
